import SearchListItem from "../SearchListItem";
import SkeletonSearchListItem from "../SearchListItem/components/SkeletonSearchListItem";
import styles from "./searchList.module.scss";
import useSearchList, { getSearchPageFromUrl, isPaginatedUrl } from "./useSearchList";
import getFilterActiveStatus, {
  getDistanceFromCityCenterStatus,
  getStayDurationAppliedStatus,
  getUniversityAppliedStatus,
} from "@Pages/Search/utils/getFilterActiveStatus";
import React, { useEffect } from "react";
import { preloadPropertyPage } from "@Pages/PropertyPage/index";
import getSubsectionIndex from "./helper";
import {
  getBreakerMapForSegment,
  shouldShowAfterCard,
  shouldShowGetHelpBeforeCard,
} from "./filteredBreakerPlacement";
import useFilteredBreakerPlans from "./useFilteredBreakerPlans";
import setCookie from "helpers/setCookie";
import { PAGE } from "constants/configConstants";
import config from "@Config/index";
import { getCurrencyFromRegion } from "@Components/LeadFormModal/utils";
import { getPricingCurrency } from "../SearchListItem/helper";
import wrapperHOC from "@Utils/wrapperHOC";
import LazyLoadOnScroll from "@Components/LazyLoadOnScroll";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import FilterSubsection from "./components/FilterSubsection";
import FilterSubsection2 from "./components/FilterSubsection2";
import useIsApp from "hooks/useIsApp";
import useIsMiniProgram from "hooks/useIsMiniProgram";
import NoAccommodationCard from "./components/NoAccommodationCard";
import useIsTabletPortrait from "hooks/useIsTabletPortrait";
import SearchPagination from "../SearchPagination";
import TrustPilotReviewsBanner from "./components/TrustPilotBanner";
import { publishWebState } from "@Utils/webApputils/nativeStateBridge";
import RadiusExpandBanner from "./components/RadiusExpandBanner";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import NearbyResultsHeading from "./components/NearbyResultsHeading";
import RecommendedBeyondFilters from "../RecommendedBeyondFilters";
import {
  getExpandAvailableCount,
  getNearbyResultsCount,
  isRadiusExpandPending,
  shouldShowRadiusExpandBanner,
} from "@Pages/Search/utils/geoRadiusExpand";

const NEARBY_ANCHOR_ID = "radius-expand-nearby-anchor";

/** Align the nearby divider with the top of the visual viewport, under sticky chrome. */
const scrollNearbySectionToStart = (scrollContainer?: HTMLElement | null) => {
  const anchor = document.getElementById(NEARBY_ANCHOR_ID);
  if (!anchor) return;

  const stickyOffset = parseFloat(window.getComputedStyle(anchor).scrollMarginTop) || 0;

  if (scrollContainer) {
    const top =
      anchor.getBoundingClientRect().top -
      scrollContainer.getBoundingClientRect().top +
      scrollContainer.scrollTop -
      stickyOffset;
    scrollContainer.scrollTo({ top: Math.max(0, top), behavior: "auto" });
    return;
  }

  // scrollBy uses the visual viewport. scrollTo(rect.top + scrollY) overshoots
  // on Android when the URL bar makes visualViewport.offsetTop non-zero, which
  // lands on the footer instead of the section start.
  window.scrollBy({ top: anchor.getBoundingClientRect().top - stickyOffset, behavior: "auto" });
};

const PreviouslyViewed = React.lazy(() => import("@Pages/Home/components/PreviouslyViewed"));
const GetHelpBannerV2 = React.lazy(() => import("./components/GetHelpBannerV2"));

interface SearchListProps {
  showGridView?: boolean;
  isMapView?: boolean;
  scrollContainer?: HTMLElement | null;
}

const RenderSubSection = ({
  children,
  noMargin,
  addMarginTop,
  noSpan,
  showGridView,
  isMapView,
}: any) => {
  if (showGridView && !isMapView) {
    return (
      <div
        className={formatClassNames(
          noSpan ? "" : styles.gridColumn,
          noMargin ? styles.zeroMarginBottom : styles.marginBottom,
          addMarginTop ? styles.marginTop : styles.zeroMarginTop,
        )}
      >
        {children}
      </div>
    );
  }
  if (isMapView) return <></>;
  return <>{children}</>;
};

const SearchList = ({
  showGridView = false,
  isMapView = false,
  scrollContainer,
}: SearchListProps) => {
  const {
    listingRef,
    handleScroll,
    items,
    isLoading,
    meta,
    region,
    filters,
    customLocation,
    whiteLabelPartnerData,
    next,
    isRadiusExpanded,
    radiusExpandSplitIndex,
    radiusExpandEnabled,
    radiusExpandPrimaryCount,
  } = useSearchList({ scrollContainer });
  const isApp = useIsApp();
  const isMiniProgram = useIsMiniProgram();
  const isTabletView = useIsTabletPortrait();
  const areFiltersApplied = getFilterActiveStatus("Filters", filters);
  const areFiltersAppliedV2 =
    getDistanceFromCityCenterStatus(filters) ||
    getUniversityAppliedStatus(filters) ||
    getStayDurationAppliedStatus(filters);

  const isFiltersSubsectionEnabled = true;
  const nearbyResultsCount = getNearbyResultsCount({
    metaCount: meta?.count,
    primaryCount: radiusExpandPrimaryCount,
    itemsLength: items?.length || 0,
    splitIndex: radiusExpandSplitIndex,
    filters,
    region,
    enabled: radiusExpandEnabled,
    isChinaApp: Boolean(config.IS_CHINA_APP),
    isWhitelabel: Boolean(config.IS_WHITELABEL_APP),
    isApp,
    isMiniProgram,
  });
  const radiusExpandPending = isRadiusExpandPending({
    isExpanded: isRadiusExpanded,
    isLoading,
    itemsLength: items?.length || 0,
    splitIndex: radiusExpandSplitIndex,
  });
  // Keep the CTA banner in place while expand is loading; only swap to the
  // divider + nearby heading once appended inventories have landed.
  // Map sidebar keeps the empty/no-matches CTA (same as mobile); hide mid-list
  // expand UI while the map is open so the narrow list stays focused on cards.
  const showRadiusExpandDivider =
    !isMapView && isRadiusExpanded && radiusExpandSplitIndex > 0 && !radiusExpandPending;
  // GrowthBook never initialises in China, so the banner shows there unflagged.
  const limitedMatchesEnabled =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SEARCH_RECOMMENDED_BEYOND_FILTERS) ||
    config.IS_CHINA_APP;
  const bannerEligible =
    radiusExpandPending ||
    shouldShowRadiusExpandBanner({
      filters,
      region,
      itemsLength: items?.length || 0,
      isLoading,
      isExpanded: isRadiusExpanded,
      metaCount: meta?.count,
      expandAvailableCount: getExpandAvailableCount(meta),
      next,
      isPaginated: isPaginatedUrl(),
      currentPage: getSearchPageFromUrl(),
      hasNonGeoFilters: areFiltersApplied,
      enabled: radiusExpandEnabled,
      limitedMatchesEnabled,
    });
  const showRadiusExpandBanner = bannerEligible && (!isMapView || (items?.length || 0) === 0);
  const useLegacyBreakerPath = Boolean(config.IS_WHITELABEL_APP || isApp || isMiniProgram);
  const filteredBreakers = useFilteredBreakerPlans({
    areFiltersApplied,
    useLegacyBreakerPath,
    isMapView,
    showGridView,
    isDesktopList: false,
    metaCount: meta?.count,
    isRadiusExpanded,
    showRadiusExpandDivider,
    radiusExpandSplitIndex,
    radiusExpandPrimaryCount,
    nearbyResultsCount,
  });
  const addSearchedLocationToLocalStorage = (place: any) => {
    try {
      if (typeof window !== "undefined" && window?.localStorage && place.id) {
        const recentlyVisited = JSON.parse(localStorage?.getItem("recentlyVisited") ?? "[]") || [];
        const searches = [...recentlyVisited];
        const index = searches?.findIndex((s) => s?.id === place?.id);
        if (index !== -1) {
          searches?.splice(index, 1);
        }

        if (searches?.length >= 8) {
          searches?.pop();
        }

        searches?.unshift(place);
        localStorage?.setItem("recentlyVisited", JSON.stringify(searches));
        publishWebState();
        setCookie({
          cname: "searchViewedFlag",
          cvalue: "true",
          exdays: 365,
        });
      }
    } catch (err) {
      console.log(err);
    }
  };
  // get formatted currency
  const regionCurrency = getCurrencyFromRegion(region?.combined_name);
  const currency = getPricingCurrency(regionCurrency);
  useEffect(() => {
    if (__CLIENT__) {
      preloadPropertyPage();
    }
  }, []);

  // Android Chrome jumps to the page footer after this CTA. Inspect and iOS keep
  // the nearby divider in view, so only correct the real Android visual viewport.
  useEffect(() => {
    if (!__CLIENT__ || !showRadiusExpandDivider || !/Android/i.test(navigator.userAgent)) {
      return undefined;
    }

    const previousOverflowAnchor = document.documentElement.style.overflowAnchor;
    document.documentElement.style.overflowAnchor = "none";

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    const run = () => scrollNearbySectionToStart(scrollContainer);
    run();
    const raf = requestAnimationFrame(run);
    const retries = [80, 300].map((ms) => window.setTimeout(run, ms));

    return () => {
      cancelAnimationFrame(raf);
      retries.forEach((id) => window.clearTimeout(id));
      document.documentElement.style.overflowAnchor = previousOverflowAnchor;
    };
  }, [showRadiusExpandDivider, scrollContainer]);

  useEffect(() => {
    addSearchedLocationToLocalStorage({
      id: region?.canonical_name,
      canonical_name: region?.canonical_name,
      name: region?.name,
      region_type: region?.region_type,
      filters,
      currency,
      name_zh: region?.name_zh ?? "",
      name_en: region?.name_en ?? "",
      ...(region?.is_whitelabel_region && {
        is_whitelabel_region: region.is_whitelabel_region,
        whitelabel_partner_ids: region.whitelabel_partner_ids ?? [],
      }),
      country: region?.location?.country?.long_name ?? "",
    });
  }, [items]);

  return (
    <div
      className={formatClassNames(
        styles.container,
        showGridView && styles.gridView,
        isMapView && styles.mapView,
      )}
      ref={listingRef}
      onScroll={handleScroll}
    >
      {items?.map((item: any, index: number) => {
        const legacySubsections = getSubsectionIndex({
          areFiltersApplied,
          areFiltersAppliedV2,
          resultCount: meta?.count,
          index,
          isFiltersSubsectionEnabled,
          isMobile: !showGridView,
          isGridView: showGridView,
          isTabletView,
        });
        const { shouldShowFilters, shouldShowFiltersV2, shouldShowRecentlyViewdSection } =
          legacySubsections;
        let { shouldShowGetHelpBanner, shouldShowTrustPilot } = legacySubsections;

        const listSplit =
          showRadiusExpandDivider && radiusExpandSplitIndex > 0
            ? radiusExpandSplitIndex
            : Number.MAX_SAFE_INTEGER;
        const isListB = index >= listSplit;
        const localIndex = isListB ? index - listSplit : index;
        const breakerMap = getBreakerMapForSegment(filteredBreakers, isListB);

        if (breakerMap) {
          // Get Help renders above the card at `index` (see the JSX below), so "after card N"
          // means "before card N + 1" here, as in SearchListDesktop (P1D-5524).
          shouldShowGetHelpBanner = shouldShowGetHelpBeforeCard(breakerMap, localIndex);
          shouldShowTrustPilot =
            shouldShowAfterCard(breakerMap, localIndex, "trustpilot") && !config.IS_CHINA_APP;
        }

        return (
          <React.Fragment key={`${item.key}_${index}`}>
            {showRadiusExpandDivider && index === radiusExpandSplitIndex && (
              <RenderSubSection
                addMarginTop={showGridView}
                showGridView={showGridView}
                isMapView={isMapView}
              >
                <div
                  id={NEARBY_ANCHOR_ID}
                  className={formatClassNames(
                    styles.nearbyAnchor,
                    showGridView && styles.fullWidthRow,
                  )}
                >
                  <RadiusExpandBanner showCard={false} />
                  {nearbyResultsCount > 0 && (
                    <NearbyResultsHeading count={nearbyResultsCount} isDesktop={showGridView} />
                  )}
                </div>
              </RenderSubSection>
            )}
            {isFiltersSubsectionEnabled && shouldShowFilters && (
              <RenderSubSection showGridView={showGridView} isMapView={isMapView}>
                <FilterSubsection />
              </RenderSubSection>
            )}
            {shouldShowGetHelpBanner && meta.count > 3 && (
              <RenderSubSection
                addMarginTop={showGridView}
                showGridView={showGridView}
                isMapView={isMapView}
              >
                <LazyLoadOnScroll
                  noLazy={showGridView}
                  fallback={<section style={{ height: "180px" }} id="GetHelpBannerV2" />}
                >
                  <GetHelpBannerV2 isDesktop={showGridView} />
                </LazyLoadOnScroll>
              </RenderSubSection>
            )}
            <SearchListItem
              index={index}
              {...item}
              regionType={region?.region_type}
              regionId={region?.id}
              region={region}
              meta={item.meta_short}
              page={PAGE.SEARCH}
              isGridView={showGridView}
              meta_real={item.meta}
              loadedCount={items.length}
              customLocation={customLocation}
              whiteLabelPartnerData={whiteLabelPartnerData}
              {...(isApp && { showCTA: false })}
            />

            {isFiltersSubsectionEnabled && shouldShowFiltersV2 && (
              <RenderSubSection showGridView={showGridView} isMapView={isMapView}>
                <FilterSubsection2 />
              </RenderSubSection>
            )}
            {shouldShowGetHelpBanner && meta.count <= 3 && (
              <RenderSubSection
                addMarginTop={showGridView}
                noMargin
                showGridView={showGridView}
                isMapView={isMapView}
              >
                <LazyLoadOnScroll
                  noLazy={showGridView}
                  fallback={<section style={{ height: "180px" }} id="GetHelpBannerV2" />}
                >
                  <GetHelpBannerV2 isDesktop={showGridView} />
                </LazyLoadOnScroll>
              </RenderSubSection>
            )}
            {shouldShowRecentlyViewdSection && (
              <RenderSubSection showGridView={showGridView} isMapView={isMapView}>
                <LazyLoadOnScroll
                  noLazy={showGridView}
                  fallback={<section style={{ height: "340px" }} id="recently-viewed" />}
                >
                  <PreviouslyViewed isDesktop={false} isHomePage={false} />
                </LazyLoadOnScroll>
              </RenderSubSection>
            )}
            {shouldShowTrustPilot && !config.IS_CHINA_APP && (
              <RenderSubSection addMarginTop showGridView={showGridView} isMapView={isMapView}>
                <TrustPilotReviewsBanner
                  uniCity={null}
                  isSearchPage
                  isHomePage={!showGridView}
                  isDesktop={showGridView}
                />
              </RenderSubSection>
            )}
          </React.Fragment>
        );
      })}
      {showRadiusExpandDivider && (items?.length || 0) <= radiusExpandSplitIndex && (
        <RenderSubSection
          addMarginTop={showGridView}
          showGridView={showGridView}
          isMapView={isMapView}
        >
          <div
            id={NEARBY_ANCHOR_ID}
            className={formatClassNames(styles.nearbyAnchor, showGridView && styles.fullWidthRow)}
          >
            <RadiusExpandBanner showCard={false} />
            {nearbyResultsCount > 0 && (
              <NearbyResultsHeading count={nearbyResultsCount} isDesktop={showGridView} />
            )}
          </div>
        </RenderSubSection>
      )}
      {items?.length === 0 && !isLoading && !showRadiusExpandBanner && <NoAccommodationCard />}
      {isLoading &&
        !radiusExpandPending &&
        [...new Array(10)].map((_it, index) => <SkeletonSearchListItem key={`loading_${index}`} />)}

      {/* SSR Trustpilot: render after list if inline position (index 17) wasn't reached */}
      {!config.IS_CHINA_APP && !filteredBreakers && items?.length > 0 && items.length <= 17 && (
        <RenderSubSection addMarginTop showGridView={showGridView} isMapView={isMapView}>
          <TrustPilotReviewsBanner
            uniCity={null}
            isSearchPage
            isHomePage={!showGridView}
            isDesktop={showGridView}
          />
        </RenderSubSection>
      )}

      {showRadiusExpandBanner && (
        <div className={showGridView ? styles.fullWidthRow : undefined}>
          <RadiusExpandBanner compact={isMapView} />
        </div>
      )}

      {/* Recommended properties beyond the exact filters — client-only, renders once exhausted */}
      <div className={showGridView ? styles.fullWidthRow : undefined}>
        <RecommendedBeyondFilters
          isGridView={showGridView}
          isMapView={isMapView}
          breakerPageState={filteredBreakers?.stateAfterAB}
          useFilteredBreakers={Boolean(filteredBreakers)}
          breakerViewMode={filteredBreakers?.viewMode}
        />
      </div>

      {/* Pagination block */}
      {items?.length > 0 && !isMapView && <SearchPagination />}
    </div>
  );
};

export default wrapperHOC(SearchList, {
  componentName: "SearchList-HeroSection-Home",
  showForChina: true,
});
